/**
 * The standing offer of the desktop app.
 *
 * This replaces the interstitial that used to stand in front of an accepted
 * invitation. The framing is the whole point of the change: the app is named
 * by *what it adds* — coding agents, your repository, worktrees, your local
 * files — not by what the browser lacks. The banner it replaces said "For the
 * full power of Nimbalyst ... download the app", which tells someone already
 * working in the console that they are using the lesser product.
 *
 * `DesktopHandoffPanel` is the same idea at a different altitude: it appears
 * when the browser genuinely cannot open a specific document. This one is the
 * ambient version, and neither ever navigates on the reader's behalf. The
 * browser cannot know whether the app is installed, so "Open in the desktop
 * app" is an offer and stays an offer — the automatic `nimbalyst://` hop is
 * exactly the first impression this plan removed.
 */

import { forwardRef, useState } from 'react';
import { DESKTOP_DOWNLOAD_URL, desktopInviteHandoffUrl } from '../auth/desktopHandoff';
import {
  invitationDeepLinkFollowed,
  invitationDownloadClicked,
} from '../auth/invitationHandoffEvents';
import type { OfferPlacement } from '../analytics/invitationAnalyticsContract';
import { BUTTON_SECONDARY_LG } from '../controlStyles';
import {
  readDesktopOfferDismissed,
  rememberDesktopOfferDismissed,
} from './desktopOfferDismissal';
import { isEmbedded } from '../embedded/embedMarker';

/** What the app adds, in the reader's terms — never what the console lacks. */
const WHAT_THE_APP_ADDS = 'coding agents, your repository, worktrees, and your local files';

const DEFAULT_LEAD = 'Nimbalyst also runs on your desktop.';

export function DesktopAppOffer({
  orgId,
  email,
  placement,
  lead = DEFAULT_LEAD,
  className,
  dismissible = false,
}: {
  /** Present only where an org is known; without it the deep link is omitted. */
  orgId?: string | null;
  email?: string;
  placement: OfferPlacement;
  /** The opening sentence. The "what it adds" clause is appended to it. */
  lead?: string;
  className?: string;
  /** Ambient placements can be turned off; an empty org keeps its offer. */
  dismissible?: boolean;
}) {
  const [dismissed, setDismissed] = useState(() => dismissible && readDesktopOfferDismissed());
  // Inside the phone app the reader already has Nimbalyst; the app owns any handoff.
  if (dismissed || isEmbedded()) return null;

  const handoffUrl = orgId ? desktopInviteHandoffUrl(orgId, email) : null;

  return (
    <div
      className={`desktop-app-offer flex flex-wrap items-center gap-x-3 gap-y-2 text-[0.85rem] leading-normal text-nim-muted ${className ?? ''}`}
      data-offer-placement={placement}
    >
      <span className="min-w-[16rem] flex-1">
        {lead} It adds <strong className="font-semibold text-nim">{WHAT_THE_APP_ADDS}</strong>.
      </span>
      <span className="flex shrink-0 items-center gap-3">
        {handoffUrl && (
          <a
            className={BUTTON_SECONDARY_LG}
            href={handoffUrl}
            onClick={() => invitationDeepLinkFollowed({ placement })}
          >
            Open in the desktop app
          </a>
        )}
        <a
          className="font-medium text-[var(--nim-primary)] no-underline hover:underline"
          href={DESKTOP_DOWNLOAD_URL}
          onClick={() => invitationDownloadClicked({ placement })}
        >
          Download
        </a>
        {dismissible && (
          <button
            className="cursor-pointer rounded border-0 bg-transparent px-1 text-[0.8rem] text-nim-faint hover:text-nim"
            type="button"
            aria-label="Dismiss the desktop app offer"
            onClick={() => {
              rememberDesktopOfferDismissed(true);
              setDismissed(true);
            }}
          >
            Not now
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * The same offer as a standing menu row.
 *
 * Both ambient placements are conditional — the welcome one only while an
 * invitation is being landed, the empty-org one only while the org is empty —
 * and the welcome one can be dismissed for good. So a member of a team that
 * already has content had no path to the app at all, which is the gap goal 2 of
 * NIM-3767 names: the console carries the app's advertisement space at any
 * time, not once.
 *
 * Deliberately not a gutter item. `navGutterItems.ts` settled that there is no
 * fifth nav slot, and this is not a place in the console to navigate to. It is
 * also not dismissible: a menu row is found by looking for it, and hiding the
 * only remaining path to the app is the failure the dismissal store already
 * says it would rather not risk.
 */
export const DesktopAppMenuItem = forwardRef<
  HTMLAnchorElement,
  { className?: string; children?: React.ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>
>(function DesktopAppMenuItem({ className, children, onClick, ...rest }, ref) {
  return (
    <a
      ref={ref}
      className={`desktop-app-menu-item ${className ?? ''}`}
      href={DESKTOP_DOWNLOAD_URL}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => {
        invitationDownloadClicked({ placement: 'gutter' });
        onClick?.(event);
      }}
      {...rest}
    >
      {children}
    </a>
  );
});
